import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import ThemeToggle from '@/components/ThemeToggle';

const USERNAME_REGEX = /^[a-zA-Z0-9_]{3,32}$/;

export default function RegisterPage() {
  const { register } = useAuth();
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const navigate = useNavigate();

  const validation = useMemo(() => {
    const errors: Record<string, string> = {};
    if (!USERNAME_REGEX.test(username)) {
      errors.username = '3–32 chars, letters, numbers, underscores only.';
    }
    if (password.length < 8) errors.password = 'At least 8 characters.';
    if (confirm !== password) errors.confirm = 'Passwords do not match.';
    return errors;
  }, [username, password, confirm]);

  const canSubmit = Object.keys(validation).length === 0;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await register(username, password, displayName.trim() || undefined);
      navigate('/', { replace: true });
    } catch (e) {
      const m = e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'Registration failed';
      setErr(m);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex h-full items-center justify-center p-6">
      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>
      <div className="card w-full max-w-sm p-6">
        <h1 className="mb-1 text-2xl font-semibold tracking-tight">Create your account</h1>
        <p className="mb-6 text-sm text-ink-500 dark:text-ink-300">It's quick — pick a username and a password.</p>
        <form onSubmit={onSubmit} className="space-y-3" noValidate>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Username</span>
            <input
              autoFocus
              name="username"
              type="text"
              autoComplete="username"
              className="input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              aria-invalid={Boolean(validation.username) && username.length > 0}
            />
            {username && validation.username && (
              <span className="mt-1 block text-xs text-red-600 dark:text-red-400">{validation.username}</span>
            )}
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Display name (optional)</span>
            <input
              name="displayName"
              type="text"
              className="input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Password</span>
            <input
              name="password"
              type="password"
              autoComplete="new-password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {password && validation.password && (
              <span className="mt-1 block text-xs text-red-600 dark:text-red-400">{validation.password}</span>
            )}
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Confirm password</span>
            <input
              name="confirm"
              type="password"
              autoComplete="new-password"
              className="input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
            {confirm && validation.confirm && (
              <span className="mt-1 block text-xs text-red-600 dark:text-red-400">{validation.confirm}</span>
            )}
          </label>
          {err && (
            <div role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/60 dark:text-red-300">
              {err}
            </div>
          )}
          <button type="submit" className="btn-primary w-full" disabled={!canSubmit || submitting} aria-busy={submitting}>
            {submitting ? 'Creating…' : 'Create account'}
          </button>
        </form>
        <p className="mt-4 text-center text-sm text-ink-500 dark:text-ink-300">
          Already have an account?{' '}
          <Link to="/login" className="text-brand-600 hover:underline dark:text-brand-300">
            Sign in
          </Link>
        </p>
      </div>
    </main>
  );
}
